<template>
  <div v-if="questionnaire && data" class="h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          class="w-full"
          :name="$t('questionnaire.title')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.title"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getInputErrors('title')"
            @blur="onInputBlur" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('questionnaire.description')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.description"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('questionnaire.traderSiteGroup')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <div v-if="mode == 'view'">{{ data.traderGroup }}</div>
          <BaseSearchDropdown
            v-else
            v-model="selectedTraderSiteGroup"
            :list-items="dropdownTraderSiteGroups"
            class="relative"
            :error="getInputErrors('traderSiteGroup')"
            :noResultsText="$t('messages.noResultsText')"
            :count="traderSiteGroupsCount"
            @clear-selection="handleTraderSiteGroupSelection"
            @updateSearch="updateTraderSiteGroupsSearch"
            @item-selected="handleTraderSiteGroupSelection">
          </BaseSearchDropdown>
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('questionnaire.questionnaireType')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="questionnaireTypes"
            :selected-item="selectedQuestionnaireType"
            flat
            :mode="mode"
            clearable
            show-error-as-tooltip
            :error="getInputErrors('type')"
            @blur="onInputBlur"
            @update:selected="handleQuestionnaireTypeSelection"
            name="questionnaireType" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('questionnaire.questionsApplication')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="filteredQuestionTypes"
            :selected-item="selectedQuestionType"
            flat
            :mode="mode"
            clearable
            @update:selected="handleQuestionsTypeSelection"
            @blur="onInputBlur"
            name="questionType" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('questionnaire.assortment')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownAssortments"
            :selected-item="selectedAssortment"
            flat
            :disable="!data.traderGroupId || data?.bindingType == 0"
            :mode="mode"
            name="assortment"
            clearable
            show-error-as-tooltip
            :error="getInputErrors('assortment')"
            @blur="onInputBlur"
            @update:selected="handleAssortmentSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('questionnaire.groupItem')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <div v-if="mode == 'view' || data.bindingType == 0">{{ data.groupItem || '-' }}</div>

          <BaseSearchDropdown
            v-else
            v-model="selectedGroupItem"
            :list-items="dropdownGroupItems"
            class=""
            :error="getInputErrors('groupItem')"
            :noResultsText="$t('messages.noResultsText')"
            :count="groupItemCount"
            @clear-selection="handleGroupItemSelection"
            @updateSearch="updateGroupItemsSearch"
            @item-selected="handleGroupItemSelection">
          </BaseSearchDropdown>
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('baseObject.sequence')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.sequence"
            type="number"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getInputErrors('sequence')"
            @blur="onInputBlur" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('questionnaire.effectiveFrom')"
          :isHorizontalLayout="isHorizontalLayout">
          <div v-if="mode == 'view'">
            {{ isoDateToFullDate(data.effectiveFromDate) || '-' }}
          </div>
          <DateTimePicker
            v-else
            ref="effectiveFromDatePickerRef"
            v-model="selectedEffectiveFromDate"
            :show-time-picker="false"
            show-error-as-tooltip
            start-empty
            :form-error="getInputErrors('effectiveFromDate')"
            @update:modelValue="onDateChange($event, 'from')">
          </DateTimePicker>
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('questionnaire.effectiveUpTo')"
          :isHorizontalLayout="isHorizontalLayout">
          <div v-if="mode == 'view'">
            {{ isoDateToFullDate(data.effectiveUpToDate) || '-' }}
          </div>
          <DateTimePicker
            v-else
            ref="effectiveUpToDatePickerRef"
            v-model="selectedEffectiveUpToDate"
            :show-time-picker="false"
            show-error-as-tooltip
            start-empty
            :form-error="getInputErrors('effectiveUpToDate')"
            @update:modelValue="onDateChange($event, 'to')">
          </DateTimePicker>
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('questionnaire.action')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownActions"
            :selected-item="selectedAction"
            flat
            :mode="mode"
            name="action"
            clearable
            @update:selected="handleActionSelection" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('questionnaire.listColumns')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.listColumns"
            type="number"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getInputErrors('noColumns')"
            @blur="onInputBlur" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus v-if="mode == 'view'" :item="data.isActive ? data.isActive : 0" />
          <BaseToggle
            v-else
            :label="$t('baseObject.isActive')"
            name="isActive"
            v-model="data.isActive" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellCheck :item="data.isFromManager ? data.isFromManager : false" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.createUser?.name || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createdDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.createdDate ? isoDateToFullDate(data.createdDate) : '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.lastUpdate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.lastUpdate ? isoDateToFullDate(data.lastUpdate) : '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.updateUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.updateUser?.name || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.boid')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.boid || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.exportDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.exportedDate ? isoDateToFullDate(data.exportedDate) : '-' }}
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import {
  isoDateToFullDate,
  isoDateToDatepickerFormat,
  datepickerToServerDateFormat,
  compareDates,
} from '@/utils/date';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, minValue, requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { enumToDropdownItems } from '@/utils/enums';

import BaseTableCellCheck from '../common/BaseTableCellCheck.vue';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseToggle from '../common/BaseToggle.vue';
import DateTimePicker from '../common/DateTimePicker.vue';

import type { SelectDropdownItem } from '@/types/Base';
import { type Questionnaire, eBindingType } from '@/types/Questionnaires';

import { getAssortments } from '@/services/api/materials';
import { getQSTActions } from '@/services/api/questionnaires';
import type { Assortment } from '@/types/Materials';
import { getGroupItemsWithMaterials, getGroupItemsWithTradeSites } from '@/services/api/groups';
import { MWQuestionnaireTypes } from '@/utils/constants';

const { t: $t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  questionnaire: Questionnaire | undefined;
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
}>();

const data = defineModel<Questionnaire>();

const dropdownQuestionTypes = ref(enumToDropdownItems(eBindingType));
const selectedQuestionType = ref<SelectDropdownItem | null>(null);

function handleQuestionsTypeSelection(item: SelectDropdownItem) {
  selectedQuestionType.value = item ? item : null;
  if (data.value) {
    data.value.bindingType = item ? (item.value as number) : null;
    if (item?.value == 0) {
      selectedAssortment.value = null;
      data.value.assortmentId = undefined;
      data.value.groupItem = undefined;
      data.value.bindingId = undefined;
    }
  }
}

const filteredQuestionTypes = computed(() => {
  switch (data.value?.mwQuestionnaireType) {
    case MWQuestionnaireTypes.MerchandisingStandardAlt:
      return dropdownQuestionTypes.value.filter((x) => x.name == 'Material groups');
    case MWQuestionnaireTypes.ForwardStock:
    case MWQuestionnaireTypes.Assortment:
      return dropdownQuestionTypes.value.filter((x) => x.name != MWQuestionnaireTypes.Generic);
    default:
      return dropdownQuestionTypes.value;
  }
});

const selectedQuestionnaireType = ref<SelectDropdownItem | null>(null);
const questionnaireTypes: SelectDropdownItem[] = [
  { 'value': MWQuestionnaireTypes.Assortment, 'name': 'Assortment' },
  { 'value': MWQuestionnaireTypes.ForwardStock, 'name': 'Forward Stock' },
  { 'value': MWQuestionnaireTypes.Generic, 'name': 'Generic' },
  { 'value': MWQuestionnaireTypes.MerchandisingStandardAlt, 'name': 'Merchandising Standard' },
];

function handleQuestionnaireTypeSelection(item: SelectDropdownItem) {
  selectedQuestionnaireType.value = item ? item : null;
  if (data.value) {
    data.value.mwQuestionnaireType = item ? (item.value as string) : '';
    data.value.isStandards = !(item?.value == MWQuestionnaireTypes.Generic);
    if (
      selectedQuestionType.value &&
      !filteredQuestionTypes.value.includes(selectedQuestionType.value)
    ) {
      handleQuestionsTypeSelection(filteredQuestionTypes.value[0]);
    }
  }
}

const assortments = ref<Assortment[]>([]);
const selectedAssortment = ref<SelectDropdownItem | null>(null);

function handleAssortmentSelection(item: SelectDropdownItem) {
  selectedAssortment.value = item;
  if (data.value) {
    data.value.assortmentId = item.value as string;
    data.value.assortment = item.name;
  }
}

const selectedTraderSiteGroup = ref<SelectDropdownItem | null>(null);
const dropdownTraderSiteGroups = ref<SelectDropdownItem[]>([]);
const traderSiteGroupsCount = ref(0);

function handleTraderSiteGroupSelection(item: SelectDropdownItem) {
  selectedTraderSiteGroup.value = item;
  if (data.value) {
    data.value.traderGroupId = item ? (item.value as string) : '';
    data.value.traderGroup = item ? item.name : undefined;
    if (data.value.assortment && data.value.assortment != item.name) {
      selectedAssortment.value = null;
      data.value.assortment = undefined;
      data.value.assortmentId = undefined;
    }
  }
  $v.value.traderSiteGroup.$touch();
}

async function updateTraderSiteGroupsSearch(searchInput: string, page?: number) {
  const items = await getGroupItemsWithTradeSites({
    page: page,
    itemsPerPage: 20,
    query: searchInput,
    sortColumn: 'name',
    sortAscending: true,
  });
  if (items.count > 0 && items.data) {
    if (page && page > 1 && dropdownTraderSiteGroups.value)
      dropdownTraderSiteGroups.value = dropdownTraderSiteGroups.value.concat(
        items.data.map((t) => ({
          value: t.id,
          name: t.ancestry,
        })) as SelectDropdownItem[]
      );
    else
      dropdownTraderSiteGroups.value = items.data.map((t) => ({
        value: t.id || '',
        name: t.ancestry,
      }));
  } else dropdownTraderSiteGroups.value = [];
  traderSiteGroupsCount.value = items.count;
}

const selectedAction = ref<SelectDropdownItem | null>(null);
const dropdownActions = ref<SelectDropdownItem[]>([]);

function handleActionSelection(item: SelectDropdownItem) {
  selectedAction.value = item ? item : null;
  if (data.value) {
    data.value.qstActionId = item ? (item.value as string) : '';
  }
}

const selectedGroupItem = ref<SelectDropdownItem | null>(null);
const dropdownGroupItems = ref<SelectDropdownItem[]>([]);
const groupItemCount = ref(0);

function handleGroupItemSelection(item: SelectDropdownItem) {
  selectedGroupItem.value = item;
  if (data.value) {
    data.value.bindingId = item ? (item.value as string) : '';
    data.value.groupItem = item ? item.name : undefined;
  }
  $v.value.groupItem.$touch();
}

async function updateGroupItemsSearch(searchInput: string, page?: number) {
  const items = await getGroupItemsWithMaterials({
    page: page,
    itemsPerPage: 20,
    query: searchInput,
    sortColumn: 'id',
    sortAscending: true,
  });
  if (items.count > 0 && items.data) {
    if (page && page > 1 && dropdownGroupItems.value)
      dropdownGroupItems.value = dropdownGroupItems.value.concat(
        items.data.map((t) => ({
          value: t.id,
          name: t.ancestry,
        })) as SelectDropdownItem[]
      );
    else
      dropdownGroupItems.value = items.data.map((t) => ({
        value: t.id || '',
        name: t.ancestry,
      }));
  } else dropdownGroupItems.value = [];
  groupItemCount.value = items.count;
}

const selectedEffectiveFromDate = ref<string>('');
const selectedEffectiveUpToDate = ref<string>('');
const effectiveFromDatePickerRef = ref();
const effectiveUpToDatePickerRef = ref();

function onDateChange(e: string | undefined, dateType: 'from' | 'to') {
  if (!data.value) return;
  if (dateType == 'from') {
    data.value.effectiveFromDate = datepickerToServerDateFormat(selectedEffectiveFromDate.value);
    $v.value.effectiveFromDate.$touch();
  } else {
    data.value.effectiveUpToDate = datepickerToServerDateFormat(selectedEffectiveUpToDate.value);
    $v.value.effectiveUpToDate.$touch();
  }
}

async function initDropdowns() {
  if (data.value) {
    if (data.value.bindingType != null) {
      const questionTypeValue = dropdownQuestionTypes.value.find(
        (item) => item.value == data.value?.bindingType
      );
      if (questionTypeValue) selectedQuestionType.value = questionTypeValue;
    }
    if (data.value.mwQuestionnaireType != null) {
      const questionnaireTypeValue = questionnaireTypes.find(
        (item) => item.value == data.value?.mwQuestionnaireType
      );
      if (questionnaireTypeValue) selectedQuestionnaireType.value = questionnaireTypeValue;
    }

    if (data.value.assortmentId != null) {
      const assortmentValue = dropdownAssortments.value.find(
        (item) => item.value == data.value?.assortmentId
      );
      if (assortmentValue) selectedAssortment.value = assortmentValue;
    }

    if (data.value.traderGroup) {
      await updateTraderSiteGroupsSearch(data.value.traderGroup, undefined);
      const traderSiteGroupValue = dropdownTraderSiteGroups.value.find(
        (item) => item.value == data.value?.traderGroupId
      );
      if (traderSiteGroupValue) selectedTraderSiteGroup.value = traderSiteGroupValue;
    } else {
      await updateTraderSiteGroupsSearch('', 1);
    }

    if (data.value.groupItem != null && data.value.bindingId) {
      await updateGroupItemsSearch(data.value.groupItem, undefined);
      const groupItemValue = dropdownGroupItems.value.find(
        (item) => item.value == data.value?.bindingId
      );
      if (groupItemValue) selectedGroupItem.value = groupItemValue;
    } else {
      await updateGroupItemsSearch('', 1);
    }
  }
}

const dropdownAssortments = computed(() => {
  const assortmentList = assortments.value || [];
  const filteredAssortments = data.value?.traderGroupId
    ? assortmentList.filter(
        ({ traderGroupItemID }) => traderGroupItemID === data.value?.traderGroupId
      )
    : assortmentList;

  return filteredAssortments.map(({ id, traderGroupItemNamePath }) => ({
    value: id,
    name: traderGroupItemNamePath,
  }));
});

async function loadDropdownData() {
  assortments.value = await getAssortments();

  const actions = await getQSTActions();
  if (actions) {
    dropdownActions.value = actions.map((x) => ({
      value: x.id,
      name: x.description,
    }));
  }
}

watch(
  () => props.mode,
  (newValue) => {
    if (newValue === 'edit') initDropdowns();
  },
  { deep: true }
);

onMounted(async () => {
  await loadDropdownData();
  await initDropdowns();
  if (data.value?.effectiveFromDate) {
    selectedEffectiveFromDate.value = isoDateToDatepickerFormat(data.value.effectiveFromDate);
  }
  if (data.value?.effectiveUpToDate) {
    selectedEffectiveUpToDate.value = isoDateToDatepickerFormat(data.value.effectiveUpToDate);
  }
  if (data.value?.action && data.value.action.description) {
    selectedAction.value = {
      name: data.value.action.description,
      value: data.value.action.id,
    };
  }
});
const isModified = ref<boolean>(false);

watch(
  () => data.value,
  (newData) => {
    validationModel.value.sequence =
      newData && newData.sequence != undefined && newData != null ? newData.sequence : undefined;
    validationModel.value.noColumns =
      newData && newData.listColumns != undefined && newData != null
        ? newData.listColumns
        : undefined;
    validationModel.value.title = newData?.title || '';
    validationModel.value.type = newData?.mwQuestionnaireType || '';
    validationModel.value.traderSiteGroup = newData?.traderGroupId || '';
    validationModel.value.groupItem = newData?.bindingId || '';
    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.questionnaire);
    validationModel.value.effectiveUpToDate = newData?.effectiveUpToDate || '';
    validationModel.value.effectiveFromDate = newData?.effectiveFromDate || '';
    validationModel.value.assortment = newData?.assortmentId || '';
    isModified.value = valueChanged;
  },
  { deep: true }
);

const validationModel = ref({
  sequence:
    data.value && data.value.sequence != undefined && data.value?.sequence != null
      ? data.value.sequence
      : undefined,
  noColumns:
    data.value && data.value.listColumns != undefined && data.value?.listColumns != null
      ? data.value.sequence
      : undefined,
  title: data.value?.title || '',
  type: data.value?.mwQuestionnaireType || '',
  traderSiteGroup: data.value?.traderGroupId || '',
  effectiveUpToDate: data.value?.effectiveUpToDate || '',
  effectiveFromDate: data.value?.effectiveFromDate || '',
  assortment: data.value?.assortmentId || '',
  groupItem: data.value?.bindingId || '',
});

const rules = computed(() => ({
  title: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  type: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  traderSiteGroup: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  sequence: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
    minValue: helpers.withMessage('Min Value is 0', minValue(0)),
  },
  noColumns: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
    minValue: helpers.withMessage('Min Value is 0', minValue(0)),
  },
  effectiveUpToDate: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
    minValue: helpers.withMessage(
      'Expiration date cannot be earlier than starting date.',
      (value: string) => {
        return data.value && data.value.effectiveFromDate
          ? compareDates(value, data.value.effectiveFromDate, '>=')
          : true;
      }
    ),
    validDate: helpers.withMessage(`${$t('messages.errors.invalidDateFormat')}`, () => {
      const validation = effectiveUpToDatePickerRef.value?.getValidationState();
      return !validation || validation.isValid;
    }),
  },
  effectiveFromDate: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
    validDate: helpers.withMessage(`${$t('messages.errors.invalidDateFormat')}`, () => {
      const validation = effectiveFromDatePickerRef.value?.getValidationState();
      return !validation || validation.isValid;
    }),
  },
  assortment: {
    required: helpers.withMessage(
      `${$t('messages.errors.required')}`,
      requiredIf(function () {
        return data.value?.mwQuestionnaireType == MWQuestionnaireTypes.Assortment;
      })
    ),
  },
  groupItem: {
    required: helpers.withMessage(
      `${$t('messages.errors.required')}`,
      requiredIf(function () {
        return data.value?.bindingType ? data.value?.bindingType > 0 : false;
      })
    ),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function getInputErrors(
  type:
    | 'sequence'
    | 'noColumns'
    | 'title'
    | 'traderSiteGroup'
    | 'effectiveUpToDate'
    | 'effectiveFromDate'
    | 'assortment'
    | 'groupItem'
    | 'type'
) {
  if ($v.value[type].required && $v.value[type].required.$invalid)
    return $v.value[type].required.$message;
  else if ($v.value[type].minValue && $v.value[type].minValue.$invalid)
    return $v.value[type].minValue.$message;
  else if ($v.value[type].requiredIf && $v.value[type].requiredIf.$invalid)
    return $v.value[type].requiredIf.$message;
  else if ($v.value[type].validDate && $v.value[type].validDate.$invalid)
    return $v.value[type].validDate.$message;
  else return '';
}

function onInputBlur() {
  $v.value.$touch();
}
</script>
